import { useCallback } from 'react';
import { create } from 'zustand';

import { sessionExpiry } from '@/lib/api/session-expiry';
import { tokenStore } from '@/lib/api/token-store';

/**
 * Each Reports tab's **applied** filters, keyed by its `<DataTable>` id, so they survive the tab
 * switch that unmounts the page (ADO 120975). Document memory only, never storage — the model
 * ADR-0016 chose for the Customer Detail hub — so a reload still starts clean.
 *
 * Filters only, deliberately not the search: 112312 requires a tab to open with an empty search
 * field and "its own default state", and page/sort stay in the URL as they were.
 */
type ReportFiltersState = {
  byTable: Record<string, Record<string, unknown>>;
  setFilters: (tableId: string, filters: Record<string, unknown>) => void;
  reset: () => void;
};

export const useReportFiltersStore = create<ReportFiltersState>((set) => ({
  byTable: {},
  setFilters: (tableId, filters) =>
    set((state) => ({ byTable: { ...state.byTable, [tableId]: filters } })),
  reset: () => set({ byTable: {} }),
}));

// An admin's filters must not greet the next one in the same tab, so the store empties when the
// session ends. It ends two ways: a logout or failed refresh clears the token store, while the
// keep-alive's 401 only raises `sessionExpiry` and leaves the stale token in place — so both.
const reset = () => useReportFiltersStore.getState().reset();
tokenStore.subscribe(() => {
  if (tokenStore.getAccessToken() === null) reset();
});
sessionExpiry.subscribe(reset);

const EMPTY: Record<string, unknown> = {};

/** The `filterState` a Reports page hands to `useListFilters`. */
export function useReportFilters<F extends Record<string, unknown>>(tableId: string) {
  const filters = useReportFiltersStore((s) => s.byTable[tableId] ?? EMPTY) as F;
  const setStored = useReportFiltersStore((s) => s.setFilters);
  const setFilters = useCallback((next: F) => setStored(tableId, next), [setStored, tableId]);
  return { filters, setFilters };
}
